import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import './styles.scss';
import { setCensusValueService } from '../../../../../services/scheduling';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { getDateFromString } from '../../../../../utility';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EnterCensusModal = ({ day, units, onClose, setPeriodData }) => {
  const formattedDate = useMemo(() => {
    const date = getDateFromString(day.date);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(date.getDate()).padStart(2, '0');
    return `${month}/${dayOfMonth} ${WEEKDAYS[date.getDay()]}`;
  }, [day.date]);

  const [censusValues, setCensusValues] = useState(() =>
    units.reduce((acc, unit) => {
      const value = unit.census_by_date?.[day.date];
      acc[unit.id] = value === undefined || value === null ? '' : String(value);
      return acc;
    }, {}),
  );
  const [isSaving, setIsSaving] = useState(false);

  const totalCensus = useMemo(() => units.reduce((sum, unit) => sum + (Number(censusValues[unit.id]) || 0), 0), [censusValues, units]);

  const handleChange = (unitId, value) => {
    const sanitized = value.replace(/\D/g, '');
    setCensusValues((state) => ({ ...state, [unitId]: sanitized }));
  };

  const handleSave = async () => {
    const censuses = units.map((unit) => ({
      scheduling_unit_id: unit.id,
      census: Number(censusValues[unit.id]) || 0,
    }));

    try {
      setIsSaving(true);
      const response = await setCensusValueService(day.id, { censuses });
      if (isResponseOk(response)) {
        setPeriodData((state) => ({
          ...state,
          units: state.units.map((unit) => {
            const item = censuses.find((c) => c.scheduling_unit_id === unit.id);
            if (!item) return unit;
            return {
              ...unit,
              census_by_date: { ...unit.census_by_date, [day.date]: item.census },
            };
          }),
        }));
        onClose();
      }
    } catch (e) {
      console.error('Failed to save census:', e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal className="modal-default enter-census-modal" isOpen onRequestClose={onClose} contentLabel="Enter Census" ariaHideApp={false}>
      <h2 className="modal-header">
        <div className="modal-header-left">
          <span className="modal-header-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="5" width="38" height="38" rx="19" fill="#F4EBFF" />
              <rect x="5" y="5" width="38" height="38" rx="19" stroke="#F9F5FF" strokeWidth="10" />
              <g clipPath="url(#clip0_16628_74097)">
                <path
                  d="M23.3335 18.6642H18.6668C18.3132 18.6642 17.9741 18.8047 17.724 19.0547C17.474 19.3048 17.3335 19.6439 17.3335 19.9975V29.3309C17.3335 29.6845 17.474 30.0236 17.724 30.2737C17.9741 30.5237 18.3132 30.6642 18.6668 30.6642H28.0002C28.3538 30.6642 28.6929 30.5237 28.943 30.2737C29.193 30.0236 29.3335 29.6845 29.3335 29.3309V24.6642M28.3335 17.6642C28.5987 17.399 28.9584 17.25 29.3335 17.25C29.7086 17.25 30.0683 17.399 30.3335 17.6642C30.5987 17.9294 30.7477 18.2891 30.7477 18.6642C30.7477 19.0393 30.5987 19.399 30.3335 19.6642L24.0002 25.9975L21.3335 26.6642L22.0002 23.9975L28.3335 17.6642Z"
                  stroke="#7F56D9"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_16628_74097">
                  <rect width="16" height="16" fill="white" transform="translate(16 16)" />
                </clipPath>
              </defs>
            </svg>
          </span>
          <span className="modal-title">Enter Census</span>
        </div>
        <button className="btn btn-icon close-btn" onClick={onClose}>
          <i className="icon-icon-x" />
        </button>
      </h2>

      <div className="enter-census-date">{formattedDate}</div>

      <div className="enter-census-units">
        {units.map((unit) => (
          <div className="enter-census-unit" key={unit.id}>
            <span className="unit-name">{unit.unit.name}</span>
            <div className="unit-census-field">
              <label>Census</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="Enter census"
                value={censusValues[unit.id]}
                onChange={(e) => handleChange(unit.id, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="enter-census-total">
        <span>Total Census</span>
        <span className="total-value">{totalCensus}</span>
      </div>

      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </button>
        <button className="btn" onClick={handleSave} disabled={isSaving}>
          Save
        </button>
      </div>
    </Modal>
  );
};

export default EnterCensusModal;
